/**
 * The slice of the web app's thread-state cache that the copied thread-list
 * adapter and metadata helpers call.
 *
 * The web app caches `threads.getState` reads in IndexedDB and revalidates
 * them on mount. The pane reads thread state straight from Aegra each time a
 * thread opens (a task pane shows one conversation at a time), so this keeps
 * only the thread-record share the adapter needs and the invalidation hooks,
 * with the same names, so the adapter stays diffable against the frontend.
 */
import type { Client, Thread } from "@langchain/langgraph-sdk";

import type { AgentThreadSummary } from "@/agents-v2/chat-runtime/types";

export type ThreadRecord = Pick<
  AgentThreadSummary,
  "metadata" | "created_at" | "updated_at" | "status"
>;

/** How long a freshly written thread record answers `getThreadRecord`. */
export const THREAD_RECORD_SHARE_MS = 3_000;

const records = new Map<string, { record: ThreadRecord; at: number }>();

const toRecord = (thread: Pick<Thread, "metadata" | "created_at" | "updated_at" | "status">): ThreadRecord => ({
  metadata: thread.metadata,
  created_at: thread.created_at,
  updated_at: thread.updated_at,
  status: thread.status
});

export const threadStateCache = {
  /**
   * Remember a thread record a write just returned, so the adapter's `fetch`
   * right after a rename or archive does not re-read the thread.
   * @param threadId - The thread the record belongs to.
   * @param thread - The record Aegra returned.
   */
  noteThreadRecord(threadId: string, thread: Pick<Thread, "metadata" | "created_at" | "updated_at" | "status">): void {
    records.set(threadId, { record: toRecord(thread), at: Date.now() });
  },
  /**
   * The thread's record: a recent write's result when there is one, else a
   * fresh read.
   * @param client - The LangGraph client.
   * @param threadId - The thread to read.
   * @returns The record.
   */
  async getThreadRecord(client: Client, threadId: string): Promise<ThreadRecord> {
    const cached = records.get(threadId);
    if (cached && Date.now() - cached.at < THREAD_RECORD_SHARE_MS) return cached.record;
    const thread = await client.threads.get(threadId);
    const record = toRecord(thread);
    records.set(threadId, { record, at: Date.now() });
    return record;
  },
  /**
   * Forget a thread (deleted, or its state moved on).
   * @param threadId - The thread to forget.
   */
  invalidate(threadId: string): void {
    records.delete(threadId);
  }
};
